import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import {
  Box, Flex, Heading, Select, Table, Thead, Tbody, Tr, Th, Td, Button, Checkbox, useColorModeValue, Text, Badge, SimpleGrid, Card, CardBody, CardHeader
} from '@chakra-ui/react';
import SummaryCard from './SummaryCard';
import { Link } from 'react-router-dom';

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString();
}

function StatusBadge({ status }) {
  let color = 'gray';
  if (status === 'passed') color = 'green';
  else if (status === 'failed') color = 'red';
  else if (status === 'skipped') color = 'yellow';
  else if (status === 'running') color = 'blue';
  return <Badge colorScheme={color} textTransform="capitalize">{status}</Badge>;
}

// Use shared SummaryCard

export default function TestHistory({ onSelectRun, onCompareRuns, selectedProject, projects, runs, loading }) {
  const [selected, setSelected] = useState([]);
  const [module, setModule] = useState('All Modules');

  // Chakra color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBg = useColorModeValue('green.50', 'gray.700');
  const tableHeaderColor = useColorModeValue('green.700', 'green.200');
  const tableRowBgEven = useColorModeValue('white', 'gray.800');
  const tableRowBgOdd = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const failColor = useColorModeValue('red.500', 'red.300');
  const passColor = useColorModeValue('green.600', 'green.200');
  const skipColor = useColorModeValue('yellow.600', 'yellow.300');
  const rowHoverBg = useColorModeValue('gray.100', 'gray.700');

  // Ensure runs is always an array
  let filteredRuns = Array.isArray(runs) ? runs : [];
  if (selectedProject && selectedProject !== 'ALL PROJECTS') {
    filteredRuns = filteredRuns.filter(r => (r.Project?.name || r.project) === selectedProject);
  }

  function toggleSelect(id) {
    setSelected(sel => sel.includes(id) ? sel.filter(x => x !== id) : sel.length < 2 ? [...sel, id] : sel);
  }
  console.log('TestHistory - Filtered runs:', filteredRuns);
  const safeRuns = filteredRuns;

  const handleCompareRuns = () => {
    if (onCompareRuns && selected.length === 2) {
      const run1 = safeRuns.find(r => r.id === selected[0]);
      const run2 = safeRuns.find(r => r.id === selected[1]);
      if (run1 && run2) {
        onCompareRuns(run1, run2); // Pass full run objects
      } else {
        console.error("Could not find selected runs for comparison. Ensure IDs are valid and runs are in safeRuns.");
      }
    }
  };

  const allModules = useMemo(() => {
    const modules = Array.from(new Set(safeRuns.flatMap(run =>
      (run.TestCases || run.testcases || []).map(tc => tc.suite || tc.file || 'Unknown')))).sort();
    modules.unshift('All Modules');
    return modules;
  }, [safeRuns]);

  // The chartData below will show all runs for the selected project unless a specific module is selected.
  // If you add a run selection filter in the future, update this logic to filter chartData accordingly.
  const chartData = useMemo(() => {
    let data = safeRuns.map(run => {
      let allRunTestCases = run.TestCases || run.testcases || [];
      let failures = 0;
      let skipped = 0;
      let total = 0;
      let passed = 0;
      if (allRunTestCases.length > 0) {
        if (module !== 'All Modules') {
          const moduleTestCases = allRunTestCases.filter(tc => (tc.suite || tc.file || 'Unknown') === module);
          failures = moduleTestCases.filter(tc => tc.failure || tc.error).length;
          skipped = moduleTestCases.filter(tc => tc.skipped !== undefined).length;
          total = moduleTestCases.length;
          passed = total - failures - skipped;
        } else {
          failures = allRunTestCases.filter(tc => tc.failure || tc.error).length;
          skipped = allRunTestCases.filter(tc => tc.skipped !== undefined).length;
          total = allRunTestCases.length;
          passed = total - failures - skipped;
        }
      } else {
        // Fallback to summary fields
        if (module === 'All Modules') {
          passed = run.passedTests || 0;
          failures = run.failedTests || 0;
          skipped = run.skippedTests || 0;
          total = run.totalTests || (passed + failures + skipped);
        } else {
          // If module filter is active but no test cases, can't break down by module
          passed = 0;
          failures = 0;
          skipped = 0;
          total = 0;
        }
      }
      const passRate = total ? (passed / total * 100) : 0;
      const flakyRate = total ? (skipped / total * 100) : 0;
      return {
        id: run.id,
        date: formatDate(run.startTime),
        passRate: Number(passRate.toFixed(1)),
        passed: passed,
        failed: failures,
        skipped: skipped,
        total: total,
        flakyRate: Number(flakyRate.toFixed(1)),
        status: run.status || (failures > 0 ? 'failed' : 'passed'),
        module: module !== 'All Modules' ? module : undefined,
      };
    }).reverse();
    return data;
  }, [safeRuns, module]);

  // Calculate average pass rate for the current module filter (matches chartData)
  const avgPassRate = useMemo(() => {
    if (!safeRuns.length) return '0.0';
    let totalPassed = 0;
    let totalTests = 0;
    safeRuns.forEach(run => {
      let passed = 0;
      let total = 0;
      // Prefer TestCases if present and non-empty
      const allRunTestCases = run.TestCases || run.testcases || [];
      if (allRunTestCases.length > 0) {
        let relevantTestCases = allRunTestCases;
        if (module !== 'All Modules') {
          relevantTestCases = allRunTestCases.filter(tc => (tc.suite || tc.file || 'Unknown') === module);
        }
        const failures = relevantTestCases.filter(tc => tc.failure || tc.error).length;
        const skipped = relevantTestCases.filter(tc => tc.skipped !== undefined).length;
        total = relevantTestCases.length;
        passed = total - failures - skipped;
      } else {
        // Fallback to summary fields
        if (module === 'All Modules') {
          passed = run.passedTests || 0;
          total = run.totalTests || 0;
        } else {
          // If module filter is active but no test cases, can't break down by module
          passed = 0;
          total = 0;
        }
      }
      totalPassed += passed;
      totalTests += total;
    });
    return totalTests ? ((totalPassed / totalTests) * 100).toFixed(1) : '0.0';
  }, [safeRuns, module]);

  // Summary stats
  const totalRuns = safeRuns.length;
  const mostRecentRun = safeRuns[0];
  // Flakiest module (highest skipped rate)
  let flakiestModule = null;
  if (safeRuns.length > 0) {
    const moduleStats = {};
    safeRuns.forEach(run => {
      (run.TestCases || run.testcases || []).forEach(tc => {
        const mod = tc.suite || tc.file || 'Unknown';
        if (!moduleStats[mod]) moduleStats[mod] = { skipped: 0, total: 0 };
        if (tc.skipped !== undefined) moduleStats[mod].skipped++;
        moduleStats[mod].total++;
      });
    });
    const sorted = Object.entries(moduleStats)
      .filter(([mod, stat]) => stat.total > 0)
      .map(([mod, stat]) => ({ mod, flaky: stat.skipped / stat.total }))
      .sort((a, b) => b.flaky - a.flaky);
    if (sorted.length > 0 && sorted[0].flaky > 0) {
      flakiestModule = `${sorted[0].mod} (${(sorted[0].flaky * 100).toFixed(1)}% skipped)`;
    }
  }

  if (loading) return <Box p={12} color={textColor}>Loading test history...</Box>;
  if (!safeRuns.length && !loading) return <Box p={12} textAlign="center" color={textColor}>No test runs found for the selected project.</Box>;

  return (
    <Box w="100%" py={8} px={{ base: 2, md: 8 }}>
      {/* Summary Cards */}
      <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4} mb={8}>
        <SummaryCard label="Total Runs" value={totalRuns} color="gray" />
        <SummaryCard label="Avg Pass Rate" value={`${avgPassRate}%`} color="green" />
        <SummaryCard label="Most Recent Run" value={mostRecentRun ? formatDate(mostRecentRun.startTime) : '-'} color="blue">
          {mostRecentRun && <StatusBadge status={mostRecentRun.status || (mostRecentRun.failedTests > 0 ? 'failed' : 'passed')} />}
        </SummaryCard>
        {flakiestModule && <SummaryCard label="Flakiest Module" value={flakiestModule} color="yellow" />}
      </SimpleGrid>

      {/* Chart Card */}
      <Card mb={8} bg={cardBg} borderRadius="lg" boxShadow="md" borderWidth={1} borderColor={cardBorder}>
        <CardHeader pb={0}><Heading size="md" color={textColor}>Test Run Trends</Heading></CardHeader>
        <CardBody>
          <Flex mb={6} align="center" gap={4} flexWrap="wrap">
            <Text fontWeight="semibold" fontSize="md" color="gray.500">Module:</Text>
            <Select
              id="moduleFilterHistory"
              value={module}
              onChange={e => setModule(e.target.value)}
              minW="180px"
              maxW="350px"
              size="md"
              bg={cardBg}
              borderColor={cardBorder}
            >
              {allModules.map(mod => <option key={mod} value={mod}>{mod}</option>)}
            </Select>
          </Flex>
          <Box w="100%" maxW="100%" minH="350px">
            {chartData && chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={cardBorder} />
                  <XAxis dataKey="date" stroke={textColor} fontSize={12} />
                  <YAxis allowDecimals={false} stroke={textColor} fontSize={12} />
                  <RechartsTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="passed" stroke="#38A169" name="Passed" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="failed" stroke="#E53E3E" name="Failed" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="skipped" stroke="#ECC94B" name="Skipped" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Flex align="center" justify="center" h="100%" color="gray.400">No chart data available for the selected module or project.</Flex>
            )}
          </Box>
        </CardBody>
      </Card>

      {/* Table Card */}
      <Card bg={cardBg} borderRadius="lg" boxShadow="md" borderWidth={1} borderColor={cardBorder}>
        <CardHeader pb={0}><Heading size="md" color={textColor}>Test Run History</Heading></CardHeader>
        <CardBody>
          <Box overflowX="auto" borderRadius="lg" display={{ base: 'none', md: 'block' }}>
            <Table variant="simple" size="md">
              <Thead bg={tableHeaderBg} position="sticky" top={0} zIndex={1}>
                <Tr>
                  <Th textAlign="center" w="48px"><Checkbox isDisabled opacity={0} /></Th>
                  <Th color={tableHeaderColor}>Run Name</Th>
                  <Th color={tableHeaderColor}>Date</Th>
                  <Th color={tableHeaderColor}>Total</Th>
                  <Th color={passColor}>Passed</Th>
                  <Th color={failColor}>Failed</Th>
                  <Th color={skipColor}>Skipped</Th>
                  <Th color={tableHeaderColor}>Status</Th>
                  <Th color={tableHeaderColor}>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {safeRuns.map((run, idx) => {
                  const isSelected = selected.includes(run.id); 
                  const failures = run.failedTests || 0;
                  const skipped = run.skippedTests || 0;
                  const total = run.totalTests || 0;
                  const passed = run.passedTests || 0;
                  const status = run.status || (failures > 0 ? 'failed' : 'passed');
                  return (
                    <Tr key={run.id} bg={idx % 2 === 0 ? tableRowBgEven : tableRowBgOdd} _hover={{ bg: rowHoverBg, cursor: 'pointer' }}>
                      <Td textAlign="center">
                        <Checkbox
                          isChecked={isSelected}
                          onChange={() => toggleSelect(run.id)}
                          isDisabled={selected.length === 2 && !isSelected}
                        />
                      </Td>
                      <Td fontWeight="medium">{run.name || run.file || run.suite || `Run #${run.id}`}</Td>
                      <Td>{formatDate(run.startTime)}</Td>
                      <Td>{total}</Td>
                      <Td color={passColor}>{passed}</Td>
                      <Td color={failColor}>{failures}</Td>
                      <Td color={skipColor}>{skipped}</Td>
                      <Td><StatusBadge status={status} /></Td>
                      <Td>
                        <Button
                          as={Link}
                          to={`/test-runs/${run.id}`}
                          colorScheme="blue"
                          variant="outline"
                          size="md"
                          borderRadius="full"
                          fontWeight="bold"
                          px={7}
                          py={2}
                          fontSize="sm"
                          _hover={{ borderColor: 'blue.600', color: 'blue.700', bg: 'white' }}
                        >
                          View Details
                        </Button>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
          {/* Mobile stacked cards */}
          <Box display={{ base: 'block', md: 'none' }}>
            {safeRuns.map((run) => {
              const failures = run.failedTests || 0;
              const skipped = run.skippedTests || 0;
              const total = run.totalTests || 0;
              const passed = run.passedTests || 0;
              const status = run.status || (failures > 0 ? 'failed' : 'passed');
              const isSelected = selected.includes(run.id);
              return (
                <Box key={run.id} borderWidth={1} borderColor={cardBorder} borderRadius="lg" p={4} mb={4} bg={cardBg}>
                  <Flex justify="space-between" align="center" mb={2}>
                    <Text fontWeight="semibold" noOfLines={2} mr={2}>{run.name || run.file || run.suite || `Run #${run.id}`}</Text>
                    <Checkbox
                      isChecked={isSelected}
                      onChange={() => toggleSelect(run.id)}
                      isDisabled={selected.length === 2 && !isSelected}
                      aria-label={`Select run ${run.id} for comparison`}
                      size="lg"
                    />
                  </Flex>
                  <Flex justify="space-between" mb={1}><Text color="gray.500">Date</Text><Text>{formatDate(run.startTime)}</Text></Flex>
                  <Flex justify="space-between" mb={1}><Text color="gray.500">Total</Text><Text>{total}</Text></Flex>
                  <Flex justify="space-between" mb={1}><Text color="gray.500">Passed</Text><Text color={passColor}>{passed}</Text></Flex>
                  <Flex justify="space-between" mb={1}><Text color="gray.500">Failed</Text><Text color={failColor}>{failures}</Text></Flex>
                  <Flex justify="space-between" mb={3}><Text color="gray.500">Skipped</Text><Text color={skipColor}>{skipped}</Text></Flex>
                  <Flex justify="space-between" align="center">
                    <StatusBadge status={status} />
                    <Button
                      as={Link}
                      to={`/test-runs/${run.id}`}
                      colorScheme="blue"
                      variant="solid"
                      size="md"
                      borderRadius="md"
                    >
                      View Details
                    </Button>
                  </Flex>
                </Box>
              );
            })}
            {selected.length === 2 && (
              <Flex mt={6} justify="flex-end">
                <Button colorScheme="green" onClick={handleCompareRuns} size="md" borderRadius="lg">Compare Selected Runs</Button>
              </Flex>
            )}
          </Box>
          {selected.length === 2 && (
            <Flex mt={6} justify="flex-end">
              <Button colorScheme="green" onClick={handleCompareRuns} size="md" borderRadius="lg">Compare Selected Runs</Button>
            </Flex>
          )}
        </CardBody>
      </Card>
    </Box>
  );
} 